(() => {
  const SELECTOR = '[data-net-reference-header]';

  function moneyFromCents(cents) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
      }).format(amount);
    } catch (_) {
      return '$' + amount.toFixed(2);
    }
  }
  window.NetReferenceHeaderMoney = moneyFromCents;

  function initDesktopSticky(root) {
    if (root.dataset.stickyMode !== 'always') return;
    const spacer = root.parentElement.querySelector('[data-net-header-spacer]');
    if (!spacer) return;

    const desktop = window.matchMedia('(min-width: 750px)');
    const pageWrapper = root.closest('.page-wrapper');
    const controller = new AbortController();
    const options = { passive: true, signal: controller.signal };
    let frame = 0;
    let compactThreshold = 1;

    function scrollPosition() {
      return pageWrapper && /auto|scroll/.test(getComputedStyle(pageWrapper).overflowY)
        ? pageWrapper.scrollTop
        : window.scrollY;
    }

    function sync() {
      frame = 0;
      if (!root.isConnected) return;
      const compact = desktop.matches && scrollPosition() >= compactThreshold;
      root.classList.toggle('is-desktop-scrolled', compact);
      if (!desktop.matches) {
        spacer.style.removeProperty('--net-fixed-header-height');
      } else if (!compact) {
        spacer.style.setProperty('--net-fixed-header-height', `${root.offsetHeight}px`);
      }
      document.body.style.setProperty('--header-height', `${root.offsetHeight}px`);
      const group = root.closest('#header-group');
      if (group) document.body.style.setProperty('--header-group-height', `${group.offsetHeight}px`);
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(sync);
    }

    function measure() {
      if (!root.isConnected) return;
      if (desktop.matches) {
        const bounds = pageWrapper?.getBoundingClientRect();
        root.style.setProperty('--net-header-left', `${bounds?.left || 0}px`);
        root.style.setProperty('--net-header-width', `${pageWrapper?.clientWidth || document.documentElement.clientWidth}px`);
        root.classList.remove('is-desktop-scrolled');
        const expandedHeight = root.offsetHeight;
        spacer.style.setProperty('--net-fixed-header-height', `${expandedHeight}px`);
        root.setAttribute('data-desktop-sticky-ready', '');
        root.classList.add('is-desktop-scrolled');
        compactThreshold = Math.max(1, expandedHeight - root.offsetHeight);
      }
      sync();
    }

    measure();
    window.addEventListener('scroll', schedule, options);
    pageWrapper?.addEventListener('scroll', schedule, options);
    window.addEventListener('resize', measure, options);
    window.addEventListener('pageshow', measure, options);
    root.addEventListener('load', measure, { capture: true, signal: controller.signal });
    document.fonts?.ready.then(measure);
    const observer = new ResizeObserver(schedule);
    observer.observe(root);
    const wrapperObserver = new ResizeObserver(measure);
    if (pageWrapper) wrapperObserver.observe(pageWrapper);
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(root)) return;
      controller.abort();
      observer.disconnect();
      wrapperObserver.disconnect();
      window.cancelAnimationFrame(frame);
    }, { signal: controller.signal });
  }

  function initHeader(root) {
    if (root.dataset.netHeaderInit === 'true') return;
    root.dataset.netHeaderInit = 'true';
    initDesktopSticky(root);

    // Prevent the old Vessel page-push state from surviving theme-editor reloads.
    document.querySelector('.page-wrapper')?.classList.remove('page-wrapper--drawer-open');

    const stickyMode = root.dataset.stickyMode;
    if (stickyMode === 'scroll-up') {
      let lastY = window.scrollY;
      root.style.position = 'sticky';
      root.style.top = '0';
      const onScroll = () => {
        const y = window.scrollY;
        const goingDown = y > lastY && y > 120;
        root.style.transform = goingDown ? 'translateY(-100%)' : 'translateY(0)';
        root.style.transition = 'transform .22s ease';
        lastY = y;
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    const form = root.querySelector('[data-net-search-form]');
    const input = root.querySelector('[data-net-search-input]');
    const panel = root.querySelector('[data-net-search-results]');
    if (!form || !input || !panel) return;

    let timer = 0;
    let controller = null;

    const icon = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>';
    const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#039;','"':'&quot;'}[c]));

    const fallbackTerms = ['Western Saddle', 'Fly Mask', 'Turnout Blankets', 'Western Belts', 'Bridles & Headstalls', 'Saddle Pads'];

    function showFallback(q = '') {
      const term = q.trim().toLowerCase();
      const matches = fallbackTerms.filter(v => !term || v.toLowerCase().includes(term)).slice(0, 5);
      if (!matches.length) {
        panel.innerHTML = '<div class="net-ref-search-results__empty">No suggestions found.</div>';
      } else {
        panel.innerHTML = matches.map(label => {
          const href = '/search?q=' + encodeURIComponent(label);
          return `<a class="net-ref-search-results__item" href="${href}">${icon}<span>${escapeHtml(label)}</span></a>`;
        }).join('');
      }
      panel.hidden = false;
    }

    async function loadSuggestions() {
      const q = input.value.trim();
      if (!q) {
        showFallback('');
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      try {
        const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const url = `${rootPath}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=6`;
        const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!res.ok) throw new Error('predictive search failed');
        const json = await res.json();
        const results = json?.resources?.results || {};
        const rows = [];
        ['products','collections','pages'].forEach(type => {
          (results[type] || []).forEach(item => {
            rows.push({ title: item.title, url: item.url || item.handle && `/${type}/${item.handle}` || '#' });
          });
        });
        if (!rows.length) {
          showFallback(q);
          return;
        }
        panel.innerHTML = rows.slice(0, 6).map(item => `<a class="net-ref-search-results__item" href="${escapeHtml(item.url)}">${icon}<span>${escapeHtml(item.title)}</span></a>`).join('');
        panel.hidden = false;
      } catch (err) {
        if (err.name !== 'AbortError') showFallback(q);
      }
    }

    input.addEventListener('focus', () => {
      if (input.value.trim()) loadSuggestions(); else showFallback('');
    });
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = window.setTimeout(loadSuggestions, 180);
    });
    input.addEventListener('keydown', e => {
      if (e.key === 'Escape') panel.hidden = true;
    });
    document.addEventListener('pointerdown', e => {
      if (!root.contains(e.target) || (!form.contains(e.target) && !panel.contains(e.target))) panel.hidden = true;
    });
  }

  function boot(scope = document) {
    scope.querySelectorAll?.(SELECTOR).forEach(initHeader);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
